import {
  useMemo,
  useState,
  useEffect,
} from 'react';
import {
  useFormState,
  useWatch,
} from 'react-hook-form';

const validateFileCount = (count) => {
  if (count === Infinity) return 1;
  if (count < 1) return 0;
  return count;
};

export const useController = ({
  name,
  control,
  clearField,
  isDisabled,
  isUploading,
  onSubmitFile,
  uploadingFieldName,
  onSubmitWithPassword,
  filesWithPassword = {},
  filesCount: filesCountProp,
}) => {
  const [filesCount, setFilesCount] = useState(validateFileCount(filesCountProp));
  const formState = useFormState({ control });

  const fieldNames = useMemo(
    () => Array.from({ length: filesCount }).map((_, index) => `${name}-${index}`),
    [filesCount, name],
  );
  const fieldPasswords = useMemo(
    () => Array.from({ length: filesCount }).map((_, index) => `${name}-${index}-password`),
    [filesCount, name],
  );

  const PASSWORD_FIELDS = useWatch({ control, name: fieldPasswords });
  const FIELD_STATE = useWatch({ control, name: fieldNames, defaultValue: {} });

  const FIELDS = useMemo(
    () => fieldNames
      .map((fieldName, index = 0) => {
        const { files, status } = FIELD_STATE[index] || {};
        if (!fieldName) return null;
        return ({
          status,
          fieldName,
          file: files?.[0],
          error: formState.errors?.[fieldName],
          password: PASSWORD_FIELDS[index],
        });
      }),
    [
      formState,
      fieldNames,
      FIELD_STATE,
      PASSWORD_FIELDS,
    ],
  );

  const currentActiveField = useMemo(
    () => {
      if (uploadingFieldName) return { fieldName: uploadingFieldName };
      const current = FIELDS
        .find((fieldState) => (fieldState?.error || !fieldState?.file));
      if (current === -1) return null;
      return current;
    },
    [
      FIELDS,
      uploadingFieldName,
    ],
  );

  const isWaitingForPassword = useMemo(
    () => Object.values(filesWithPassword).reduce((current, next) => next || current, false),
    [filesWithPassword],
  );

  const fields = useMemo(() => FIELDS
    .filter(Boolean)
    .map(({
      file,
      error,
      status,
      password,
      fieldName,
    }, index) => {
      const isHiddenField = currentActiveField?.fieldName !== fieldName;
      const displayAskForPassword = filesWithPassword[fieldName] === true;
      const fileName = file?.name;
      const submitWithPassword = () => {
        onSubmitWithPassword({
          fieldName,
          file,
          password,
        });
      };

      const onChange = (e) => {
        onSubmitFile(fieldName, e.target.files[0]);
      };

      const removeFile = () => {
        clearField({ fieldName });
      };

      return {
        file,
        index,
        error,
        status,
        onChange,
        fieldName,
        isHiddenField,
        displayAskForPassword,
        fileNames: fileName,
        onRemoveFile: removeFile,
        isAlertLoading: isUploading,
        isFieldDisabled: isDisabled || isUploading || isWaitingForPassword,
        onSubmitWithPassword: submitWithPassword,
      };
    }), [
    FIELDS,
    isDisabled,
    clearField,
    isUploading,
    onSubmitFile,
    filesWithPassword,
    onSubmitWithPassword,
    isWaitingForPassword,
    currentActiveField?.fieldName,
  ]);

  const isLastFieldFilled = useMemo(
    () => {
      const missingInputExist = FIELDS.some((field) => !field?.file);
      if (missingInputExist) return false;
      return true;
    },
    [FIELDS],
  );

  useEffect(() => {
    if (filesCountProp !== Infinity) setFilesCount(validateFileCount(filesCountProp));
    else if (isLastFieldFilled) setFilesCount((prev) => prev + 1);
  }, [
    filesCountProp,
    isLastFieldFilled,
  ]);

  return {
    fields,
  };
};
